/**
 * targeting.ts — pure helpers for combat target selection.
 *
 * Given a firing ship (the "scanner" — it both looks and shoots) and the
 * team vision map that covers what it knows about, pick the best enemy to
 * aim at. The v1 rule is simple and deliberately deterministic:
 *
 *   1. Target must be currently observed via the team's own eyes (a row in
 *      teamVision with seenVia !== null). Ghost records inside the grace
 *      window coast visually but we don't shoot at a position we haven't
 *      seen this tick — that would feel like sci-fi magic to the player.
 *   2. Target must be in the same system as the scanner, alive
 *      (currentHull > 0), and not mid-jump (systemId !== null).
 *   3. Distance scanner → target must be ≤ `weaponRangeEffective`, which
 *      the caller computes as `weapon.range × state.balanceData.combat.weaponRangeScale`.
 *   4. Prefer vision-channel records over scan-channel (full icon > blip).
 *   5. Within a channel, pick the nearest target. Ties broken by ascending
 *      ship-id, giving the combat pass bit-exact reproducibility under a
 *      fixed seed — the whole sim relies on this property (MULTIPLAYER §2).
 *
 * This module is side-effect-free. It doesn't read cloaked/disabled/etc.
 * on the scanner — "can this ship shoot at all?" is a concern for the
 * combat pass, not for targeting. Keep the two layers separable.
 */

import type {
  GameState,
  ShipInstance,
  VisibilityRecord,
} from '../state/types';

export interface TargetCandidate {
  /** The enemy ship we intend to fire on. */
  ship: ShipInstance;
  /** The vision record that produced the aim point. */
  record: VisibilityRecord;
  /** Euclidean distance (local 0..1 coords) from scanner to target. */
  distance: number;
}

/**
 * Pick the best target for `scanner` to fire on with a weapon of effective
 * in-system range `weaponRangeEffective`. Returns `null` when no eligible
 * enemy exists in the team's vision map.
 *
 * Iteration walks the team's vision records in ascending `shipId` order so
 * tie-breaks on distance are deterministic. Uses strict `<` when comparing
 * candidates so the first one encountered (lowest id) stays "best" on
 * ties, matching the ascending-id iteration.
 */
export function pickTarget(
  state: GameState,
  scanner: ShipInstance,
  weaponRangeEffective: number,
): TargetCandidate | null {
  if (scanner.systemId === null) return null;
  if (!(weaponRangeEffective > 0)) return null;

  const teamMap = state.teamVision.get(scanner.ownerEmpireId);
  if (!teamMap || teamMap.size === 0) return null;

  const rangeSq = weaponRangeEffective * weaponRangeEffective;

  // Deterministic iteration: sort record keys ascending. Small enough N
  // (team's tracked-enemy count) that the sort cost is negligible per tick.
  const records = [...teamMap.values()].sort((a, b) => a.shipId - b.shipId);

  let bestVision: TargetCandidate | null = null;
  let bestScan: TargetCandidate | null = null;

  for (const rec of records) {
    // Ghost records (grace-window stragglers) have no live aim point this
    // tick — skip them entirely.
    if (rec.seenVia === null) continue;
    if (rec.lastSeenSystemId !== scanner.systemId) continue;

    const target = state.ships.get(rec.shipId);
    if (!target) continue;
    if (target.currentHull <= 0) continue;
    if (target.systemId === null) continue; // mid-jump
    if (target.systemId !== scanner.systemId) continue;

    const dx = target.sx - scanner.sx;
    const dy = target.sy - scanner.sy;
    const distSq = dx * dx + dy * dy;
    if (distSq > rangeSq) continue;
    const distance = Math.sqrt(distSq);

    const cand: TargetCandidate = { ship: target, record: rec, distance };

    if (rec.seenVia === 'vision') {
      if (bestVision === null || distance < bestVision.distance) {
        bestVision = cand;
      }
    } else {
      // 'scan' channel — only interesting if no vision hit wins.
      if (bestScan === null || distance < bestScan.distance) {
        bestScan = cand;
      }
    }
  }

  return bestVision ?? bestScan;
}
